import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Sparkles,
  Target,
  Upload,
  X,
} from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { GoalStatus } from '@/db/types';
import {
  GOAL_STATUS_OPTIONS,
  getGoalTotals,
  listGoalsWithStats,
  type GoalTotals,
  type GoalWithStats,
} from '@/lib/goals';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

const STATUS_COLOR: Record<GoalStatus, { bg: string; tint: string; label: string }> = {
  someday: { bg: '#EAF0EA', tint: Palette.forest, label: 'Someday' },
  planning: { bg: 'rgba(225,163,61,0.18)', tint: Palette.honeyDeep, label: 'Planning' },
  'in-progress': { bg: 'rgba(176,95,60,0.15)', tint: Palette.clay, label: 'In progress' },
  done: { bg: '#DCE7E1', tint: Palette.moss, label: 'Done' },
};

const PRIORITY_DOTS: Record<number, string> = {
  1: '●●●',
  2: '●●○',
  3: '●○○',
};

export default function GoalsScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [items, setItems] = useState<GoalWithStats[]>([]);
  const [totals, setTotals] = useState<GoalTotals>({
    count: 0,
    someday: 0,
    planning: 0,
    inProgress: 0,
    done: 0,
    totalCostCents: 0,
  });
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<GoalStatus | 'all'>('all');

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((g) => {
      if (statusFilter !== 'all' && g.status !== statusFilter) return false;
      if (!q) return true;
      // Search across title, category, why, notes — the freeform fields a
      // user is most likely to look for ("the kitchen", "solar", etc).
      const hay = `${g.title ?? ''} ${g.category ?? ''} ${g.why ?? ''} ${g.notes ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, statusFilter]);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    setItems(await listGoalsWithStats(db, prop.id));
    setTotals(await getGoalTotals(db, prop.id));
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)/my-home')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Projects & dreams</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <Pressable
            onPress={() => router.push('/projects/import' as never)}
            style={({ pressed }) => [styles.addOutline, pressed && { opacity: 0.85 }]}
            accessibilityLabel="Import CSV">
            <Upload size={16} color={Palette.forest} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => router.push('/projects/new' as never)}
            style={({ pressed }) => [styles.add, pressed && { opacity: 0.85 }]}>
            <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        </View>
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
        <View style={styles.hero}>
          <View style={styles.heroIcon}>
            <Sparkles size={20} color={Palette.honey} strokeWidth={1.8} />
          </View>
          <Text style={styles.heroTitle}>What do you want this place to become?</Text>
          <Text style={styles.heroSub}>
            Park every dream and active project here — a deck rebuild, a refinanced
            solar setup, a she-shed in the back. Status tags + target dates turn
            fuzzy "someday"s into real projects, and projects into a real roadmap.
          </Text>
          <View style={styles.statRow}>
            <Stat label="Tracked" value={String(totals.count)} />
            <Stat
              label="In progress"
              value={String(totals.inProgress)}
              accent={totals.inProgress > 0}
            />
            <Stat
              label="Est. budget"
              value={
                totals.totalCostCents > 0
                  ? `$${(totals.totalCostCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                  : '—'
              }
            />
          </View>
        </View>

        {/* Search + status filter chips. Search hits title, category,
            why, notes — the fuzzy freeform fields. Filter chips narrow by
            status; "all" is the default. Both filters compose. */}
        {items.length > 0 ? (
          <View style={styles.filterWrap}>
            <View style={styles.searchBox}>
              <Search size={15} color={Palette.inkSoft} strokeWidth={2} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search goals & projects…"
                placeholderTextColor={Palette.inkFaint}
                autoCorrect={false}
                style={[styles.searchInput, webNoOutline]}
              />
              {query ? (
                <Pressable onPress={() => setQuery('')} hitSlop={8}>
                  <X size={14} color={Palette.inkFaint} strokeWidth={2} />
                </Pressable>
              ) : null}
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.chipRow}>
              <Pressable
                onPress={() => setStatusFilter('all')}
                style={({ pressed }) => [
                  styles.statusFilterChip,
                  statusFilter === 'all' && styles.statusFilterChipActive,
                  pressed && { opacity: 0.85 },
                ]}>
                <Text
                  style={[
                    styles.statusFilterText,
                    statusFilter === 'all' && styles.statusFilterTextActive,
                  ]}>
                  All · {items.length}
                </Text>
              </Pressable>
              {GOAL_STATUS_OPTIONS.map((s) => {
                const count = items.filter((g) => g.status === s.v).length;
                if (count === 0) return null;
                const active = statusFilter === s.v;
                return (
                  <Pressable
                    key={s.v}
                    onPress={() => setStatusFilter(active ? 'all' : s.v)}
                    style={({ pressed }) => [
                      styles.statusFilterChip,
                      active && styles.statusFilterChipActive,
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Text
                      style={[
                        styles.statusFilterText,
                        active && styles.statusFilterTextActive,
                      ]}>
                      {s.label} · {count}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        ) : null}

        {items.length === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emptyTitle}>Nothing here yet</Text>
            <Text style={styles.emptyBody}>
              Tap + above to drop in your first dream or kick off a project.
              Start with whatever's loudest in your head — refine it later.
            </Text>
            <Pressable
              onPress={() => router.push('/projects/new' as never)}
              style={({ pressed }) => [styles.emptyCta, pressed && { transform: [{ scale: 0.98 }] }]}>
              <Plus size={16} color={Palette.cream} strokeWidth={2.2} />
              <Text style={styles.emptyCtaText}>Add a goal or project</Text>
            </Pressable>
          </View>
        ) : (
          <View style={{ paddingHorizontal: 18, gap: 10, marginTop: 4 }}>
            {GOAL_STATUS_OPTIONS.map((s) => {
              const group = filteredItems.filter((g) => g.status === s.v);
              if (group.length === 0) return null;
              return (
                <View key={s.v} style={{ marginTop: Spacing.three }}>
                  <Text style={styles.groupHead}>{s.label.toUpperCase()} · {group.length}</Text>
                  <View style={{ gap: 8 }}>
                    {group.map((g) => (
                      <GoalCard
                        key={g.id}
                        goal={g}
                        onPress={() => router.push(`/projects/${g.id}` as never)}
                      />
                    ))}
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function GoalCard({ goal, onPress }: { goal: GoalWithStats; onPress: () => void }) {
  const tone = STATUS_COLOR[goal.status];
  const stepsLabel =
    goal.steps_total > 0 ? `${goal.steps_done}/${goal.steps_total} steps` : null;
  const stepsPct =
    goal.steps_total > 0 ? Math.round((goal.steps_done / goal.steps_total) * 100) : 0;
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.card, pressed && { transform: [{ scale: 0.99 }] }]}>
      {/* Two-column layout: image (or placeholder) on the left, all the
          metadata stacked tight on the right. Compact height keeps the
          list scannable on mobile but the image still adds visual recall
          on desktop. */}
      <View style={styles.cardRow}>
        {goal.photo_uri ? (
          <Image source={{ uri: goal.photo_uri }} style={styles.cardThumb} />
        ) : (
          <View style={[styles.cardThumb, styles.cardThumbEmpty]}>
            <Target size={20} color={Palette.forest} strokeWidth={1.5} />
          </View>
        )}
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={styles.cardHead}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.cardTitle} numberOfLines={2}>{goal.title}</Text>
              {goal.category ? (
                <Text style={styles.cardCategory}>{goal.category}</Text>
              ) : null}
            </View>
            <View style={[styles.statusChip, { backgroundColor: tone.bg }]}>
              <Text style={[styles.statusChipText, { color: tone.tint }]}>{tone.label}</Text>
            </View>
          </View>
          {goal.why ? (
            <Text style={styles.cardWhy} numberOfLines={2}>{goal.why}</Text>
          ) : null}
          {stepsLabel ? (
            <View style={styles.stepsBar}>
              <View style={styles.stepsTrack}>
                <View style={[styles.stepsFill, { width: `${stepsPct}%` }]} />
              </View>
              <Text style={styles.stepsLabel}>{stepsLabel}</Text>
            </View>
          ) : null}
          <View style={styles.cardFoot}>
            {goal.target_date ? (
              <View style={styles.metaRow}>
                <CalendarDays size={12} color={Palette.inkSoft} strokeWidth={1.8} />
                <Text style={styles.metaText}>
                  {new Date(goal.target_date).toLocaleDateString(undefined, {
                    month: 'short',
                    year: 'numeric',
                  })}
                </Text>
              </View>
            ) : null}
            {goal.est_cost_cents ? (
              <View style={styles.metaRow}>
                <Target size={12} color={Palette.inkSoft} strokeWidth={1.8} />
                <Text style={styles.metaText}>
                  ${(goal.est_cost_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Text>
              </View>
            ) : null}
            <Text style={styles.priority}>{PRIORITY_DOTS[goal.priority] ?? PRIORITY_DOTS[2]}</Text>
            <View style={{ flex: 1 }} />
            <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.7} />
          </View>
        </View>
      </View>
    </Pressable>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <View style={styles.stat}>
      <Text style={[styles.statValue, accent && { color: Palette.clay }]}>{value}</Text>
      <Text style={styles.statLabel}>{label.toUpperCase()}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  add: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  addOutline: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  hero: {
    marginHorizontal: 18, marginTop: Spacing.three,
    backgroundColor: Palette.card, borderRadius: Radius.xl,
    borderWidth: 1, borderColor: Palette.line, padding: 18,
  },
  heroIcon: {
    width: 42, height: 42, borderRadius: 13,
    backgroundColor: 'rgba(225,163,61,0.18)',
    alignItems: 'center', justifyContent: 'center',
    marginBottom: 10,
  },
  heroTitle: {
    fontFamily: Fonts.serifBold, fontSize: 20, color: Palette.ink, letterSpacing: -0.3,
  },
  heroSub: {
    marginTop: 6, fontSize: 13, lineHeight: 19,
    color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  statRow: {
    flexDirection: 'row', gap: 18, marginTop: Spacing.three,
  },
  stat: {},
  statValue: {
    fontFamily: Fonts.serifBold, fontSize: 22, color: Palette.ink, letterSpacing: -0.4,
  },
  statLabel: {
    marginTop: 2, fontSize: 11, fontFamily: Fonts.sansBold,
    color: Palette.inkFaint, letterSpacing: 0.6,
  },
  groupHead: {
    fontFamily: Fonts.sansBold, fontSize: 11.5, color: Palette.inkFaint,
    letterSpacing: 0.7, marginBottom: 8,
  },
  cardRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  cardThumb: {
    width: 64,
    height: 64,
    borderRadius: 12,
    backgroundColor: '#EAF0EA',
    flexShrink: 0,
  },
  cardThumbEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Palette.line,
  },
  filterWrap: {
    marginTop: Spacing.three,
    marginHorizontal: 18,
    gap: 8,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
    padding: 0,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 6,
    paddingRight: 16,
  },
  statusFilterChip: {
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  statusFilterChipActive: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  statusFilterText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.inkSoft,
    letterSpacing: 0.3,
  },
  statusFilterTextActive: {
    color: Palette.cream,
  },
  card: {
    backgroundColor: Palette.card, borderRadius: Radius.lg,
    borderWidth: 1, borderColor: Palette.line, padding: 14,
  },
  cardHead: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  cardTitle: {
    fontFamily: Fonts.serifBold, fontSize: 16, color: Palette.ink,
    letterSpacing: -0.2, lineHeight: 21,
  },
  cardCategory: {
    marginTop: 2, fontFamily: Fonts.sansBold, fontSize: 12,
    color: Palette.inkFaint, letterSpacing: 0.4,
  },
  statusChip: {
    paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999,
  },
  statusChipText: {
    fontFamily: Fonts.sansBold, fontSize: 11.5, letterSpacing: 0.4,
  },
  cardWhy: {
    marginTop: 8, fontFamily: Fonts.sansMedium, fontSize: 13.5,
    color: Palette.inkSoft, lineHeight: 18,
  },
  stepsBar: {
    marginTop: 10,
    flexDirection: 'row', alignItems: 'center', gap: 8,
  },
  stepsTrack: {
    flex: 1, height: 5, borderRadius: 4,
    backgroundColor: Palette.sand,
    overflow: 'hidden',
  },
  stepsFill: { height: '100%', backgroundColor: Palette.forest, borderRadius: 4 },
  stepsLabel: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkSoft,
    letterSpacing: 0.2,
  },
  cardFoot: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    marginTop: 10,
  },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  metaText: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.inkSoft,
  },
  priority: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.honeyDeep,
    letterSpacing: 1,
  },
  empty: {
    marginHorizontal: 18, marginTop: Spacing.four,
    alignItems: 'center', paddingHorizontal: 18,
  },
  emptyTitle: { fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink },
  emptyBody: {
    marginTop: 6, fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkSoft, textAlign: 'center', lineHeight: 19,
  },
  emptyCta: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    marginTop: 14, backgroundColor: Palette.forest,
    paddingHorizontal: 16, paddingVertical: 11, borderRadius: 14,
  },
  emptyCtaText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
});
